import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  articleData?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
  schema?: Record<string, unknown>;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  articleData,
  schema,
}: SeoHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    const linkEl = document.querySelector('link[rel="canonical"]');
    if (linkEl) {
      linkEl.setAttribute('href', canonicalUrl);
    } else {
      const newLink = document.createElement('link');
      newLink.rel = 'canonical';
      newLink.href = canonicalUrl;
      document.head.appendChild(newLink);
    }

    if (articleData?.publishedTime) {
      setMeta('article:published_time', articleData.publishedTime);
    }
    if (articleData?.modifiedTime) {
      setMeta('article:modified_time', articleData.modifiedTime);
    }
    if (articleData?.author) {
      setMeta('article:author', articleData.author);
    }

    if (schema) {
      const existingSchema = document.querySelector('script[data-seo-schema]');
      if (existingSchema) existingSchema.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, articleData, schema]);

  return null;
}